// StoryScrollScreen.jsx
// Provides the single-scroll storytelling experience that moves visitors from belief to action.
import { useState } from 'react';
import PageLayout from '../components/PageLayout';
import AboutLocalVoiceDialog from '../components/AboutLocalVoiceDialog';
import { scrollToSection } from '../utils/scroll';
import { track } from '../firebase';

function Section({ children, className = '' }) {
  return (
    <section className={`mx-auto max-w-3xl sm:max-w-4xl px-4 mt-10 sm:mt-20 sm:px-6 ${className}`}>
      {children}
    </section>
  );
}

function SectionHeader({ title, children }) {
  return (
    <header className="text-center mb-6 sm:mb-8">
      <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 [text-wrap:balance]">
        {title}
      </h2>
      {children ? (
        <p className="mx-auto mt-3 max-w-2xl text-base sm:text-lg text-slate-700 leading-relaxed [text-wrap:balance]">
          {children}
        </p>
      ) : null}
    </header>
  );
}


const HERO_CTA = {
  label: 'Find an advocacy group near you',
  href: '#groups',
};

/**
 * recordButtonClick
 * Sends a Google Analytics event for Story Scroll button interactions.
 */
function recordButtonClick(action, params = {}) {
  track('story_button_click', {
    page: 'story_scroll',
    action,
    ...params,
  });
}

/**
 * reportOutboundConversion
 * Fires the Google Ads conversion snippet for outbound advocacy clicks and returns
 * the snippet result so callers can decide whether to cancel default navigation.
 * When allowDefaultNavigation is true, the helper always returns true so the
 * browser can continue handling the click (for example, to open a new tab).
 */
function reportOutboundConversion(destinationUrl, options = {}) {
  const { allowDefaultNavigation = false } = options;

  if (typeof window === 'undefined') {
    return true;
  }

  const conversion = window.gtag_report_conversion;
  if (typeof conversion === 'function') {
    const result = conversion(destinationUrl);

    if (allowDefaultNavigation) {
      return true;
    }

    return result;
  }

  return true;
}

/**
 * handleCardLinkClick
 * Delegates anchor clicks, scrolling for in-page links and tracking outbound conversions.
 * Options:
 * - trackConversion: fire the Google Ads snippet for outbound URLs
 * - allowDefaultNavigation: let the browser keep handling navigation even if the snippet requests otherwise
 * - skipConversionRedirect: avoid passing the URL to the snippet so it does not redirect after firing
 */
function handleCardLinkClick(event, href, options = {}) {
  if (!href) {
    return;
  }

  const {
    trackConversion = false,
    allowDefaultNavigation = false,
    skipConversionRedirect = false,
  } = options;

  recordButtonClick('card_link_click', { href });

  if (href.startsWith('#')) {
    const targetId = href.slice(1);
    scrollToSection(event, targetId);
    return;
  }

  if (!trackConversion) {
    return;
  }

  const conversionUrl = skipConversionRedirect ? undefined : href;
  const result = reportOutboundConversion(conversionUrl, { allowDefaultNavigation });
  if (result === false && event && !allowDefaultNavigation) {
    event.preventDefault();
  }
}

/**
 * StoryScrollScreen
 * Renders the entire scroll narrative including hero, stories, groups, and bills.
 */
export default function StoryScrollScreen({ bills = [], groups = [], barrierStories = [], content }) {
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);
  const topic = content?.topic ?? '';
  const heroContent = content?.heroContent ?? '';
  const lastUpdated = content?.lastUpdated ?? '';

  const handleFooterLinkClick = (event, source) => {
    event.preventDefault();
    recordButtonClick('footer_link_click', { source });
    setIsAboutDialogOpen(true);
  };

  const handleCloseAboutDialog = () => {
    setIsAboutDialogOpen(false);
  };

  return (
    <PageLayout>
      <HeroSection heroContent={heroContent} />
      <BarrierStories stories={barrierStories} topic={topic} />
      <GroupsSection groups={groups} bills={bills} topic={topic} />
      <BillsSection bills={bills} groups={groups} topic={topic} />
      <ClosingSection topic={topic} />
      <StoryScrollFooter
        onAboutClick={(event) => handleFooterLinkClick(event, 'about-localvoice')}
        onFeedbackClick={(event) => handleFooterLinkClick(event, 'feedback')}
        lastUpdated={lastUpdated}
      />
      <AboutLocalVoiceDialog open={isAboutDialogOpen} onClose={handleCloseAboutDialog} />
    </PageLayout>
  );
}

/**
 * HeroSection
 * Introduces the page with headline, subhead, and quick navigation.
 */
function HeroSection({ heroContent }) {
  const {description, preGradientText, gradientText, postGradientText} = heroContent;
  const cta = HERO_CTA;

  const handleHeroCtaClick = (event) => {
    if (!cta.href) return;
    recordButtonClick('hero_find_group', { href: cta.href });
    if (cta.href.startsWith('#')) scrollToSection(event, cta.href.slice(1));
  };

  return (
    <Section>
      <div className="text-center">
        <HeroTitle preGradientText={preGradientText} gradientText={gradientText} postGradientText={postGradientText} />
        {description ? (
          <p className="mx-auto mt-5 max-w-2xl text-lg sm:text-2xl text-slate-700 leading-relaxed [text-wrap:balance]">
            {description}
          </p>
        ) : null}
        {cta.label && cta.href ? (
          <div className="mt-10 flex justify-center">
            <a
              href={cta.href}
              onClick={handleHeroCtaClick}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-700"
            >
              {cta.label}
              <span aria-hidden="true">↓</span>
            </a>
          </div>
        ) : null}
      </div>
    </Section>
  );
}


/**
 * HeroTitle
 * Renders the hero headline with a gradient highlight and topic-aware closing phrase.
 */
function HeroTitle({ gradientText, preGradientText, postGradientText }) {
  return (
    <h1 className="mt-3 text-4xl sm:text-6xl font-semibold text-slate-900 [text-wrap:balance]">
      {preGradientText}{' '}
      <span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500 bg-clip-text text-transparent">
        {gradientText}
      </span>{' '}
      {postGradientText}
    </h1>
  );
}

/**
 * BarrierStories
 * Displays the affirmative barrier stories as individual cards.
 */
function BarrierStories({ stories, topic }) {
  if (!stories?.length) return null;

  return (
    <Section className="mb-8">
      <SectionHeader title="Real case studies, real results">
        Learn how Massachusetts residents turned ideas into laws.
      </SectionHeader>
      <div className="space-y-10 sm:space-y-12">
        {stories.map((story) => (
          <BarrierStoryCard key={story.key} story={story} />
        ))}
      </div>
    </Section>
  );
}


/**
 * BarrierStoryCard
 * Renders a single barrier story card with headline derived from the takeaway.
 */
function BarrierStoryCard({ story }) {
  if (!story) return null;

  return (
    <article className="rounded-3xl bg-white p-5 sm:p-7 shadow-sm ring-1 ring-slate-200">
      <header className="text-center">
        <h3 className="mt-1 text-xl sm:text-2xl font-semibold text-slate-900 [text-wrap:balance]">
          {story.takeaway}
        </h3>
      </header>
      <div className="mt-6">
        <BarrierEntry story={story} />
      </div>
    </article>
  );
}


/**
 * BarrierEntry
 * Shows a single example with accordion-style detail.
 */
function BarrierEntry({ story }) {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => {
    const next = !isOpen;
    recordButtonClick('barrier_details_toggle', { entryTitle: story?.title, expanded: next });
    setIsOpen(next);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-6">
      <button
        onClick={toggle}
        className="w-full text-left flex flex-col gap-4 sm:flex-row focus:outline-none"
      >
        <div className="flex w-full flex-col sm:w-56">
          <img
            src={story.imageUrl}
            alt={story.imageAlt}
            className="h-44 w-full rounded-xl object-cover sm:h-36"
          />
          {story.imageSource ? ( <p className="mt-2 text-xs text-slate-500">Photo by {story.imageSource}</p> ) : null}
        </div>
        <div className="flex-1">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-indigo-600">{story.title}</p>
          <p className="mt-2 text-base sm:text-lg text-slate-800 leading-relaxed">{story.caption}</p>
          <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-indigo-700">
            <span>{isOpen ? 'Hide how they did it' : 'How they did it'}</span>
            <span aria-hidden="true">{isOpen ? '–' : '+'}</span>
          </div>
        </div>
      </button>

      {isOpen ? (
        <BarrierDetails details={story.details} sources={story.sources} className="mt-4 sm:mt-6" />
      ) : null}
    </div>
  );
}

/**
 * BarrierDetails
 * Lists the supporting detail when expanded and cites the supporting sources when available.
 */
function BarrierDetails({ details, sources, className }) {
  if (!details?.length) return null;

  const containerClassName = className ?? 'mt-3';
  const shouldShowSources = false;

  return (
    <div className={containerClassName}>
      <ul className="list-disc space-y-2 pl-5 text-[15px] sm:text-base text-slate-700 leading-relaxed">
        {details.map((detail) => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
      {shouldShowSources ? null : null}
    </div>
  );
}


/**
 * GroupsSection
 * Highlights advocacy groups with the bills they support.
 */
function GroupsSection({ groups, bills, topic }) {
  const billsById = bills.reduce((accumulator, bill) => {
    accumulator[bill.id] = bill;
    return accumulator;
  }, {});

  return (
    <section id="groups">
      <header className="text-center">
        <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl pt-10 [text-wrap:balance]">Explore local advocacy groups</h2>
        <p className="mx-auto mt-3 max-w-2.5xl text-base text-slate-700 sm:text-lg">
          Connecting with an advocacy group is the best way to learn how residents make change.
        </p>
      </header>
      <div className="mt-8 space-y-4">
        {groups.map((group) => (
          <GroupCard key={group.slug} group={group} billsById={billsById} />
        ))}
      </div>
    </section>
  );
}

/**
 * GroupCard
 * Displays group summary, logo placeholder, and supported bills.
 */
function GroupCard({ group, billsById }) {
  return (
    <article
      id={`group-${group.slug}`}
      className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{group.name}</h3>
          <p className="mt-1 text-sm text-slate-700">{group.summary}</p>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="items-center gap-2 text-sm text-slate-700">
          <div className="font-medium text-slate-900">Focus areas:</div>
          {(group.supports || []).map((billId) => {
            const bill = billsById[billId];
            if (!bill) {
              return null;
            }

            const billHref = `#bill-${bill.id}`;

            return (
              <a
                key={billId}
                href={billHref}
                onClick={(event) => handleCardLinkClick(event, billHref)}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 mx-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 mt-2"
              >
                <span>{bill.short_summary_gerund}</span>
              </a>
            );
          })}
        </div>
        {/* External join link stays in a new tab so readers can keep the story open. */}
        <a
          href={group.joinUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(event) =>
            handleCardLinkClick(event, group.joinUrl, {
              trackConversion: true,
              allowDefaultNavigation: true,
              skipConversionRedirect: true,
            })
          }
          className="inline-flex items-center gap-2 self-start rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
        >
          Learn more about this group
          <span aria-hidden="true">↗</span>
        </a>
      </div>
    </article>
  );
}

/**
 * BillsSection
 * Repurposes the bill content with anchors for cross-links.
 */
function BillsSection({ bills, groups, topic }) {
  const groupSlugByName = groups.reduce((acc, g) => ((acc[g.name] = g.slug), acc), {});

  return (
    <Section id="bills">
      <SectionHeader title="Learn about local legislation">
        Example bills that show how advocacy connects to policy in Massachusetts.
      </SectionHeader>

      <div className="mt-8 space-y-6 sm:space-y-8">
        {bills.map((bill) => (
          <BillCard key={bill.id} bill={bill} groupSlugByName={groupSlugByName} />
        ))}
      </div>
    </Section>
  );
}


/**
 * BillCard
 * Summarizes a bill with toggled bullets and cross-links to groups.
 */
function BillCard({ bill, groupSlugByName }) {
  const [showBullets, setShowBullets] = useState(false);
  const primarySupporter = bill.supporters?.[0];
  const primarySlug = primarySupporter ? groupSlugByName[primarySupporter.name] : null;

  return (
    <article id={`bill-${bill.id}`} className="rounded-2xl bg-white p-6 sm:p-7 ring-1 ring-slate-200 shadow-sm">
      <BillNumberGroup numbers={bill.numbers} onNavigate={handleCardLinkClick} />
      <header className="flex flex-wrap items-center gap-3">
        <h3 className="text-lg sm:text-xl font-semibold text-slate-900">{bill.short_summary}</h3>
        <button
          type="button"
          onClick={() => {
            const next = !showBullets;
            recordButtonClick('bill_details_toggle', { billId: bill.id, expanded: next });
            setShowBullets(next);
          }}
          className="shrink-0 text-sm font-medium text-indigo-700 hover:text-indigo-900"
        >
          {showBullets ? 'Less info' : 'More info'}
        </button>
      </header>

      {bill.bullets?.length ? (
        <div className="mt-3">{showBullets ? <BillBullets bullets={bill.bullets} /> : null}</div>
      ) : null}

      <BillSupporters supporters={bill.supporters} groupSlugByName={groupSlugByName} />

      {primarySlug ? (
        <div className="mt-5">
          <a
            href={`#group-${primarySlug}`}
            onClick={(event) => handleCardLinkClick(event, `#group-${primarySlug}`)}
            className="inline-flex items-center gap-2 rounded-full border border-indigo-600 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
          >
            Learn more from related organizations
            <span aria-hidden="true">↑</span>
          </a>
        </div>
      ) : null}
    </article>
  );
}


/**
 * BillNumberGroup
 * Displays the available bill numbers and separates them when both are present.
 */
function BillNumberGroup({ numbers, onNavigate }) {
  if (!numbers?.house && !numbers?.senate) {
    return null;
  }

  return (
    <div className="mb-4 flex items-center gap-2">
      {numbers.house ? (
        <BillNumber billNumber={numbers.house} onNavigate={onNavigate} />
      ) : null}
      {numbers.house && numbers.senate ? (
        <span className="text-sm text-slate-500">/</span>
      ) : null}
      {numbers.senate ? (
        <BillNumber billNumber={numbers.senate} onNavigate={onNavigate} />
      ) : null}
    </div>
  );
}

/**
 * BillNumber
 * Formats a bill number as an external link.
 */
function BillNumber({ billNumber, onNavigate }) {
  const handleClick = (event) => {
    recordButtonClick('bill_number_click', {
      label: billNumber.label,
      url: billNumber.url,
    });
    if (onNavigate) {
      onNavigate(event, billNumber.url);
    }
  };

  return (
    <a
      href={billNumber.url}
      target="_blank"
      rel="noreferrer"
      onClick={handleClick}
      className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
    >
      {billNumber.label}
    </a>
  );
}

/**
 * BillBullets
 * Displays the expanded bullet list for a bill.
 */
function BillBullets({ bullets }) {
  return (
    <ul className="my-2 list-disc space-y-2 pl-5 text-sm text-slate-700 sm:text-base">
      {bullets.map((bullet) => (
        <li key={bullet}>{bullet}</li>
      ))}
    </ul>
  );
}

/**
 * BillSupporters
 * Lists supporters with anchors back to their group cards. When a supporter lacks a slug,
 * the joinUrl becomes an outbound link that should report an ad conversion.
 */
function BillSupporters({ supporters, groupSlugByName }) {
  if (!supporters?.length) {
    return null;
  }

  return (
    <div>
      <div className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3 mt-3">Related advocacy groups</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {supporters.map((supporter) => {
          const slug = groupSlugByName[supporter.name];
          const href = slug ? `#group-${slug}` : supporter.joinUrl;
          const isExternalSupporterLink = !slug && Boolean(supporter.joinUrl);

          // External supporters use the join URL directly, which is when we fire an ad conversion.

          return (
            <a
              key={supporter.name}
              href={href}
              onClick={(event) =>
                handleCardLinkClick(event, href, { trackConversion: isExternalSupporterLink })
              }
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
            >
              <span>{supporter.name}</span>
              <span aria-hidden="true" className="text-sm">
                {slug ? '↑' : '↗'}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

/**
 * ClosingSection
 * Encourages a final action at the end of the scroll.
 */
function ClosingSection({ topic }) {
  const cta = { label: 'Start exploring', href: '#groups' };
  const steps = ['Explore a group near you.', 'Learn what they do.', "Share what you've learned with a friend."];

  const handleClosingCtaClick = (event) => {
    if (!cta.href) return;
    recordButtonClick('closing_take_step', { href: cta.href });
    if (cta.href.startsWith('#')) scrollToSection(event, cta.href.slice(1));
  };

  return (
    <Section className="mb-20">
      <div className="rounded-3xl bg-indigo-600 px-6 py-10 sm:px-10 text-center text-white">
        <h2 className="text-3xl sm:text-4xl font-semibold [text-wrap:balance]">Your first step to get involved</h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-indigo-100 leading-relaxed">Every victory began with residents showing up and connecting in their communities</p>
        {steps.length ? (
          <ol className="mx-auto mt-6 max-w-xl space-y-3 text-left text-base sm:text-lg">
            {steps.map((step, index) => (
              <li key={step} className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-sm font-semibold text-indigo-700">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        ) : null}
        {cta.label && cta.href ? (
          <div className="mt-7 flex justify-center">
            <a
              href={cta.href}
              onClick={handleClosingCtaClick}
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
            >
              {cta.label}
              <span aria-hidden="true">↑</span>
            </a>
          </div>
        ) : null}
      </div>
    </Section>
  );
}

/**
 * StoryScrollFooter
 * Presents footer links to learn about LocalVoice and leave feedback.
 */
function StoryScrollFooter({ onAboutClick, onFeedbackClick, lastUpdated }) {
  const showLastUpdated = Boolean(lastUpdated);

  return (
    <footer className="mt-12 border-t border-slate-200 pt-6 text-center">
      <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-slate-600">
        <FooterLink onClick={onAboutClick}>About LocalVoice</FooterLink>
        <span aria-hidden="true" className="text-slate-400">
          •
        </span>
        <FooterLink onClick={onFeedbackClick}>Give us feedback</FooterLink>
        {showLastUpdated ? (
          <>
            <span aria-hidden="true" className="text-slate-400">
              •
            </span>
            <span>Last updated {lastUpdated}</span>
          </>
        ) : null}
      </div>
    </footer>
  );
}

/**
 * FooterLink
 * Renders a footer anchor that triggers the supplied click handler.
 */
function FooterLink({ onClick, children }) {
  return (
    <a
      href="#about-localvoice"
      onClick={onClick}
      className="font-medium text-indigo-700 hover:text-indigo-900"
    >
      {children}
    </a>
  );
}
